/**
 * The MAIN-THREAD client. This is the object UI holds.
 *
 * It owns the four things a caller must not have to remember:
 *   • the reqId ledger and the promise for each in-flight request
 *   • the allocator seed across a `worker.terminate()` + respawn (adapter §4)
 *   • the transfer lists, so bytes move instead of copying
 *   • the VERSION HANDSHAKE, which fails LOUDLY rather than producing documents the page
 *     cannot read
 *
 * It touches `Worker` and `postMessage` and nothing else — no DOM, no pixels. Turning bytes
 * into a download is UI's; `outputName`/`archiveName` in core/adapter/filenames.ts give it
 * the name, and the note in `download()` below gives it the one rule that matters.
 */

/// <reference lib="webworker" />

import { CONTRACT_VERSION, SCHEMA_VERSION } from '../../contracts/cue-model.ts';
import type { Diagnostic, DocumentId, InputId } from '../../contracts/cue-model.ts';
import type {
  FontsMessage,
  MainToWorker,
  PinyinMessage,
  RequestKind,
  ResultTypes,
  WorkerToMain,
} from '../core/protocol.ts';
import { isFireAndForget, transferablesOf } from '../core/protocol.ts';
import { mintEpoch } from '../core/ids/allocator.ts';

export class WorkerVersionMismatch extends Error {
  readonly workerContract: string;
  readonly workerSchema: number;
  constructor(workerContract: string, workerSchema: number) {
    super(
      `worker/page version mismatch: the worker bundle is contract ${workerContract} schema ${workerSchema}, ` +
        `this page is contract ${CONTRACT_VERSION} schema ${SCHEMA_VERSION}. ` +
        'A cached worker against a fresh page is a real deployment state; reload with a cache bust.',
    );
    this.name = 'WorkerVersionMismatch';
    this.workerContract = workerContract;
    this.workerSchema = workerSchema;
  }
}

export class WorkerFailure extends Error {
  readonly reason: string;
  readonly detail: unknown;
  constructor(reason: string, detail: unknown) {
    super(`worker failure: ${reason}`);
    this.name = 'WorkerFailure';
    this.reason = reason;
    this.detail = detail;
  }
}

export class WorkerCrash extends Error {
  constructor(message: string, stack?: string) {
    super(message);
    this.name = 'WorkerCrash';
    if (stack !== undefined) this.stack = stack;
  }
}

export interface ClientOptions {
  /** The bundled worker URL. UI supplies it; CORE does not choose a path. */
  readonly url: string | URL;
  /** false in a production build — INVARIANTS are asserted at pipeline boundaries in dev only. */
  readonly devAssertions?: boolean;
  readonly onProgress?: (reqId: number, done: number, total: number, phase: string) => void;
  readonly onDiagnostics?: (diagnostics: readonly Diagnostic[]) => void;
  /** Called when the worker dies or is respawned, so the UI can re-issue what it lost. */
  readonly onRespawn?: (reason: 'terminate' | 'error') => void;
}

/** Per-request extras. Every field is optional, so a bare `request(kind, payload)` is unchanged. */
export interface RequestOptions {
  /**
   * THIS request's diagnostics, called just before its promise resolves; `[]` when there were
   * none. `ClientOptions.onDiagnostics` still sees every batch, but carries no reqId, so it
   * cannot tell a caller which of its requests a diagnostic belongs to. A tool page needs
   * exactly that: an `apply` whose op the engine refused ('invalid-op-parameters') returns the
   * document UNCHANGED, and only the diagnostic says the result is not what was asked for.
   */
  readonly onDiagnostics?: (diagnostics: readonly Diagnostic[]) => void;
}

interface Pending {
  readonly resolve: (value: never) => void;
  readonly reject: (error: Error) => void;
  readonly kind: RequestKind;
  readonly onDiagnostics?: (diagnostics: readonly Diagnostic[]) => void;
}

export class SubtitleWorkerClient {
  readonly #options: ClientOptions;
  /** Minted ONCE PER PAGE and never re-minted. A respawn reuses it. */
  readonly #epoch: string = mintEpoch();
  /** The maximum counter any worker has reported. Passed back in `init` on respawn, which is
   *  what makes it impossible for a restarted worker to re-issue a live id. */
  #allocatorCounter = 0;
  #worker: Worker | null = null;
  #nextReqId = 1;
  readonly #pending = new Map<number, Pending>();
  #readyPromise: Promise<void> | null = null;
  #resolveReady: (() => void) | null = null;
  #rejectReady: ((e: Error) => void) | null = null;

  constructor(options: ClientOptions) {
    this.#options = options;
  }

  get epoch(): string {
    return this.#epoch;
  }

  get allocatorCounter(): number {
    return this.#allocatorCounter;
  }

  get running(): boolean {
    return this.#worker !== null;
  }

  /** Spawns the worker and waits for a version-checked `ready`. Idempotent. */
  async start(): Promise<void> {
    const existing = this.#readyPromise;
    if (existing !== null) return existing;
    this.#spawn();
    // #spawn always assigns it; the local read is what convinces the compiler.
    const spawned = this.#readyPromise as Promise<void> | null;
    if (spawned === null) throw new Error('worker failed to spawn');
    return spawned;
  }

  #spawn(): void {
    const worker = new Worker(this.#options.url, { type: 'module' });
    this.#worker = worker;
    this.#readyPromise = new Promise<void>((resolve, reject) => {
      this.#resolveReady = resolve;
      this.#rejectReady = reject;
    });
    worker.onmessage = (event: MessageEvent<WorkerToMain>): void => this.#receive(event.data);
    worker.onerror = (event: ErrorEvent): void => {
      const error = new WorkerCrash(event.message);
      this.#rejectReady?.(error);
      this.#failAll(error);
      this.#options.onRespawn?.('error');
    };
    this.#post({
      kind: 'init',
      reqId: 0,
      epoch: this.#epoch,
      allocatorCounter: this.#allocatorCounter,
      ...(this.#options.devAssertions === undefined
        ? {}
        : { devAssertions: this.#options.devAssertions }),
    });
  }

  #receive(message: WorkerToMain): void {
    switch (message.kind) {
      case 'ready': {
        if (
          message.contractVersion !== CONTRACT_VERSION ||
          message.schemaVersion !== SCHEMA_VERSION
        ) {
          const error = new WorkerVersionMismatch(message.contractVersion, message.schemaVersion);
          this.#rejectReady?.(error);
          this.#failAll(error);
          this.terminate();
          return;
        }
        this.#resolveReady?.();
        return;
      }
      case 'result': {
        this.#allocatorCounter = Math.max(this.#allocatorCounter, message.allocatorCounter);
        if (message.diagnostics.length > 0) this.#options.onDiagnostics?.(message.diagnostics);
        const pending = this.#pending.get(message.reqId);
        this.#pending.delete(message.reqId);
        pending?.onDiagnostics?.(message.diagnostics);
        pending?.resolve(message.value as never);
        return;
      }
      case 'progress':
        this.#options.onProgress?.(message.reqId, message.done, message.total, message.phase);
        return;
      case 'failure': {
        const pending = this.#pending.get(message.reqId);
        this.#pending.delete(message.reqId);
        pending?.reject(new WorkerFailure(message.reason, message.detail));
        return;
      }
      case 'crash': {
        const pending = this.#pending.get(message.reqId);
        this.#pending.delete(message.reqId);
        pending?.reject(new WorkerCrash(message.message, message.stack));
        return;
      }
    }
  }

  #post(message: MainToWorker): void {
    const worker = this.#worker;
    if (worker === null) throw new Error('worker is not running; call start() first');
    const transfer = transferablesOf(message);
    if (transfer.length > 0) worker.postMessage(message, transfer as unknown as Transferable[]);
    else worker.postMessage(message);
  }

  #failAll(error: Error): void {
    for (const p of this.#pending.values()) p.reject(error);
    this.#pending.clear();
  }

  /**
   * Sends a request and resolves with its typed result.
   *
   * NOTE ON BYTES: any `Uint8Array` in the message is TRANSFERRED, so the caller's view is
   * DETACHED once this returns. Read whatever you need from it first — its `byteLength`
   * comes back on the result for exactly this reason.
   */
  async request<K extends RequestKind>(
    kind: K,
    payload: Omit<Extract<MainToWorker, { kind: K }>, 'kind' | 'reqId'>,
    options: RequestOptions = {},
  ): Promise<ResultTypes[K]> {
    await this.start();
    const reqId = this.#nextReqId++;
    const message = { kind, reqId, ...payload } as unknown as MainToWorker;
    const promise = new Promise<ResultTypes[K]>((resolve, reject) => {
      this.#pending.set(reqId, {
        resolve: resolve as (v: never) => void,
        reject,
        kind,
        ...(options.onDiagnostics ? { onDiagnostics: options.onDiagnostics } : {}),
      });
    });
    this.#post(message);
    return promise;
  }

  /**
   * Fire-and-forget, generic. PREFER THE NAMED METHODS BELOW.
   *
   * All five fire-and-forget kinds now have one, because a hand-built message passed through
   * here is a door nobody knows is there: `fonts` and `pinyin` reached the worker ONLY this
   * way, `notify()` had zero callers in `src/` or `web/`, and so both channels were dead — the
   * font bytes could not reach the PDF writer and the pinyin table could not reach `PinyinOp`.
   * Two of the most expensive features in the product, gated on a method nobody called.
   *
   * That is F5's shape a fourth time: a thing that is built, correct, tested and unreachable.
   * A named method is what makes a channel discoverable by autocomplete rather than by reading
   * the protocol.
   */
  notify(message: MainToWorker): void {
    if (!isFireAndForget(message.kind)) {
      throw new RangeError(`${message.kind} expects a result; use request() instead`);
    }
    this.#post(message);
  }

  /**
   * Sends font bytes for the PDF writer to embed. §6, and `adapter.md` §3's `fonts` row.
   *
   * MUST BE RESIDENT BEFORE A WRITE STARTS: `RunContext.resources.font()` is SYNCHRONOUS, so
   * the worker cannot fetch on demand mid-render. Send these before requesting a PDF, not
   * during. Without them the writer refuses styled non-Latin output rather than shipping tofu
   * — which is every document containing CJK, Cyrillic, Greek, Polish, Vietnamese, Turkish or
   * emoji, not only the scripts descoped for launch.
   *
   * The bytes are TRANSFERRED; the caller's views detach. Read anything you need first.
   */
  sendFonts(fonts: FontsMessage['fonts']): void {
    if (this.#worker === null) return;
    this.#post({ kind: 'fonts', reqId: 0, fonts });
  }

  /**
   * Sends the Hanzi→reading table. §9, and `adapter.md` §3's `pinyin` row.
   *
   * Several hundred KB, so it is lazy-loaded on first `PinyinOp` and never bundled into every
   * worker. Until it arrives, `validateOp` REJECTS a `PinyinOp` by name rather than passing the
   * text through unchanged — contract 1.2.0 froze that, because a pinyin tool that quietly
   * returns its input looks like it worked.
   */
  sendPinyinTable(table: PinyinMessage['table']): void {
    if (this.#worker === null) return;
    this.#post({ kind: 'pinyin', reqId: 0, table });
  }

  /**
   * COOPERATIVE cancel. Works between units — files in a batch, ops in a pipeline, chunks of
   * cues. It cannot interrupt one indivisible unit; for that, `terminateAndRespawn()`.
   */
  cancel(reqId: number | 'all' = 'all'): void {
    if (this.#worker === null) return;
    this.#post({ kind: 'cancel', reqId: 0, target: reqId });
  }

  dispose(docIds?: readonly DocumentId[], inputIds?: readonly InputId[]): void {
    if (this.#worker === null) return;
    this.#post({
      kind: 'dispose',
      reqId: 0,
      ...(docIds === undefined ? {} : { docIds }),
      ...(inputIds === undefined ? {} : { inputIds }),
    });
  }

  terminate(): void {
    this.#worker?.terminate();
    this.#worker = null;
    this.#readyPromise = null;
    this.#resolveReady = null;
    this.#rejectReady = null;
  }

  /**
   * THE HARD CANCEL — the only thing that stops a run already inside one indivisible unit.
   *
   * Terminating loses the worker's documents, its Measure memo and its allocator counter.
   * The counter is the one that matters, and it is safe: this client has been keeping the
   * maximum reported on every result, and passes it straight back in `init`. A restarted
   * worker therefore cannot re-issue an id a live document already holds.
   *
   * Everything else the caller must re-send. `onRespawn` fires so it knows to.
   */
  async terminateAndRespawn(): Promise<void> {
    const error = new WorkerCrash('the worker was terminated to cancel an indivisible run');
    this.#failAll(error);
    this.terminate();
    this.#options.onRespawn?.('terminate');
    this.#spawn();
    await this.#readyPromise;
  }

  /**
   * A single download, for UI to call.
   *
   * THE ONE RULE: this must run INSIDE THE USER-GESTURE TASK. Deferring it past an `await` is
   * what makes downloads silently fail in Safari — so the bytes have to be in hand BEFORE the
   * click handler runs, or the click has to be the thing that awaits nothing.
   *
   * Left here as a documented helper rather than called from the engine: it touches the DOM,
   * which is UI's boundary, and UI may want its own revoke timing.
   */
  static downloadBlueprint(): string {
    return [
      'const url = URL.createObjectURL(new Blob([bytes], { type }));',
      'const a = document.createElement("a");',
      'a.href = url; a.download = filename; a.rel = "noopener";',
      'document.body.appendChild(a); a.click(); a.remove();',
      'setTimeout(() => URL.revokeObjectURL(url), 0);',
    ].join('\n');
  }
}
